import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import './AddFamily.css';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import PageHeader from './PageHeader';
import AxiosPost from '../http/Axios';

// Define month to number mapping
const monthNameToNumber = {
  January: '01',
  February: '02',
  March: '03',
  April: '04',
  May: '05',
  June: '06',
  July: '07',
  August: '08',
  September: '09',
  October: '10',
  November: '11',
  December: '12',
};

const AddFamily = () => {
  const inactivityMinutes =
    parseInt(process.env.REACT_APP_INACTIVITY_MINUTES || "20", 10);

  useInactivityTimer(inactivityMinutes);
  const dropdownRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [familyName, setFamilyName] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const loggedInAs = sessionStorage.getItem("loggedInAs") || "admin";

  useEffect(() => {
    document.title = 'LeapFrog Lab Registration Admin Portal';
  }, []);

  const [familyData, setFamilyData] = useState({
    familyName: '',
    referredBy: '',
    okayToContactIndicator: 'Y',
    guardianFirstName: '',
    guardianLastName: '',
    relationshipToChild: 'Mother',
    email: '',
    primaryPhone: '',
    zipCode: '',
    childFirstName: '',
    gender: '',
    monthOfBirth: '',
    yearOfBirth: '',
    whichHandId: 'Right-handed',
    languageSpokenAtHome: 'English',
    additionalLanguage1: '',
    additionalLanguage2: '',
    registeredIndicator: 'Y',
    registeredDate: new Date(),
    disabledIndicator: 'N',
    createdDate: new Date(),
    createdBy: loggedInAs,
    modifiedDate: new Date(),
    modifiedBy: loggedInAs,
    familyEmail: '',
    isPrimaryIndicator: 'Y',
  });

  const [errors, setErrors] = useState({
    familyName: '',
    guardianFirstName: '',
    guardianLastName: '',
    email: '',
    primaryPhone: '',
    zipCode: '',
    childFirstName: '',
  });

  const numericMonthOfBirth =
    monthNameToNumber[familyData.monthOfBirth] || familyData.monthOfBirth;

  const familyEmail = familyData.familyEmail;
  const zipCode = familyData.zipCode;
  const [phone, setPhone] = useState('');

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavigation = (path) => {
    navigate(`${path}`);
  };

  const handleLogout = async (e) => {
    e?.preventDefault();
    try {
      await fetch('/kidlab-reg/admin/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout API failed:', err);
    }

    sessionStorage.clear();
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const handleCancel = () => {
    navigate(`/search-page`);
  };

  const validate = () => {
    const errors = {};

    if (!familyData.familyName.match("[a-zA-Z .'-]+")) {
      errors.familyName = 'Family name is invalid.';
    }

    if (!familyData.guardianFirstName.match("[a-zA-Z .'-]+")) {
      errors.guardianFirstName = 'Guardian first name is invalid.';
    }

    if (!familyData.guardianLastName.match("[a-zA-Z .'-]+")) {
      errors.guardianLastName = 'Guardian last name is invalid.';
    }

    if (!familyData.childFirstName.match("[a-zA-Z .'-]+")) {
      errors.childFirstName = 'Child first name is invalid.';
    }

    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;

    if (!phoneRegex.test(familyData.primaryPhone)) {
      errors.primaryPhone = 'Primary phone is invalid.';
    }

    if (!validateEmail(familyEmail)) {
      errors.familyEmail = 'Email format is invalid.';
    }

    if (!validateZipCode(familyData.zipCode)) {
      errors.zipCode = 'ZIP code should be 5 digits.';
    }

    if (
      familyData.referredBy === '' ||
      familyData.referredBy === 'Select Reference'
    ) {
      errors.referredBy = 'Please select a valid reference.';
    }

    if (
      familyData.relationshipToChild === '' ||
      familyData.relationshipToChild === 'Select Relationship'
    ) {
      errors.relationshipToChild = 'Please select a valid relationship.';
    }

    if (familyData.gender === '' || familyData.gender === 'Select Gender') {
      errors.gender = 'Please select a valid gender.';
    }

    if (
      familyData.monthOfBirth === '' ||
      familyData.monthOfBirth === 'Select Month'
    ) {
      errors.monthOfBirth = 'Please select a valid month of birth.';
    }

    if (
      familyData.yearOfBirth === '' ||
      familyData.yearOfBirth === 'Select Year'
    ) {
      errors.yearOfBirth = 'Please select a valid year of birth.';
    }

    if (
      familyData.whichHandId === '' ||
      familyData.whichHandId === 'Select Preference'
    ) {
      errors.whichHandId = 'Please select a valid preference.';
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      console.log('Validation failed:', errors);
      return;
    }

    const familyPostData = {
      email: familyEmail,
      zipCode,
      familyName: familyData.familyName,
      referredBy: familyData.referredBy,
      okayToContactIndicator: familyData.okayToContactIndicator,
      registeredIndicator: 'Y',
      registeredDate: new Date(),
      disabledIndicator: 'N',
      createdDate: new Date(),
      createdBy: loggedInAs,
      modifiedDate: new Date(),
      modifiedBy: loggedInAs,
      eml: familyEmail,
    };

    const guardianPostData = {
      firstName: familyData.guardianFirstName,
      lastName: familyData.guardianLastName,
      email: familyEmail,
      primaryPhone: familyData.primaryPhone,
      zipCode: zipCode,
      relationshipToChild: familyData.relationshipToChild,
      registeredIndicator: 'Y',
      registeredDate: new Date(),
      disabledIndicator: 'N',
      createdDate: new Date(),
      createdBy: loggedInAs,
      modifiedDate: new Date(),
      modifiedBy: loggedInAs,
      eml: familyEmail,
      familyEmail: familyEmail,
      isPrimaryIndicator: 'Y',
    };

    const childPostData = {
      email: familyEmail,
      firstName: familyData.childFirstName,
      gender: familyData.gender,
      monthOfBirth: numericMonthOfBirth,
      yearOfBirth: familyData.yearOfBirth,
      whichHandId: familyData.whichHandId,
      languageSpokenAtHome: familyData.languageSpokenAtHome,
      additionalLanguage1: familyData.additionalLanguage1,
      additionalLanguage2: familyData.additionalLanguage2,
      specialNeeds: "",
      registeredIndicator: 'Y',
      registeredDate: new Date(),
      disabledIndicator: 'N',
      createdDate: new Date(),
      createdBy: loggedInAs,
      modifiedDate: new Date(),
      modifiedBy: loggedInAs,
      mob: familyData.monthOfBirth,
      eml: familyEmail,
    };

    try {
      const familyResponse = await AxiosPost(
        process.env.REACT_APP_ADMIN_SERVICE + '/families',
        familyPostData
      );

      await AxiosPost(
        process.env.REACT_APP_ADMIN_SERVICE + '/guardians',
        guardianPostData
      );
      await AxiosPost(
        process.env.REACT_APP_ADMIN_SERVICE + '/children',
        childPostData
      );

      const familyDetailsPath = '/family-details-page';

      const guidResponse = await axios.get(
        process.env.REACT_APP_ADMIN_SERVICE + `/families/guid?email=${encodeURIComponent(familyEmail)}`
      );

      const guid = guidResponse.data;

      navigate(
        `/family-details-page?guid=${guid}`
      );
    } catch (error) {
      console.error('Error submitting data:', error);
    }
  };

  const handleFamilyChange = (e) => {
    const { name, value } = e.target;

    setFamilyData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleGuardianChange = (e) => {
    const { name, value } = e.target;

    setFamilyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChildChange = (e) => {
    const { name, value } = e.target;

    setFamilyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  function validateEmail(familyEmail) {
    if (typeof familyEmail !== 'string') {
      console.error('Email is not a string:', familyEmail);
      return false;
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(familyEmail);
  }

  const validateZipCode = (zipCode) => {
    const zipCodeRegex = /^\d{5}$/; // 5 digits only
    return zipCodeRegex.test(zipCode);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = ('' + value).replace(/\D/g, '');
    // Check if the cleaned string has at least 10 digits
    if (cleaned.length >= 10) {
      // Format the phone number as (xxx) xxx-xxxx
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    return cleaned;
  };

  const handlePhoneChange = (event) => {
    const formattedPhone = formatPhoneNumber(event.target.value);
    setPhone(formattedPhone);
    setFamilyData((prevData) => ({
      ...prevData,
      primaryPhone: formattedPhone,
    }));
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    let years = [];
    for (let i = 0; i < 11; i++) {
      years.push(
        <option key={i} value={currentYear - i}>
          {currentYear - i}
        </option>
      );
    }
    return years;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="dashboard add-family">
      <PageHeader title="Add New Family" />

      <form onSubmit={handleSubmit} className="family-form">
        <main className="page-content">
          {/* Family Section */}
          <div className="section">
            <div className="input-grid">
              <div className="input-group">
                <label htmlFor="familyName">
                  Family name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="familyName"
                  value={familyData.familyName}
                  onChange={handleFamilyChange}
                  required
                  maxLength="31"
                  pattern="[a-zA-Z .'-]+"
                  title="Family name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."
                  className="input-size"
                />
                {errors.familyName && (
                  <p className="error-message">{errors.familyName}</p>
                )}
              </div>

              <div className="input-group">
                <label>Okay to contact?</label>
                <select
                  name="okayToContactIndicator"
                  value={familyData.okayToContactIndicator ? 'Y' : 'N'}
                  onChange={handleFamilyChange}
                  className="input-size"
                >
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="referredBy">
                  Referred by {' '}
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="referredBy"
                  value={familyData.referredBy}
                  onChange={handleFamilyChange}
                  className="input-size"
                >
                  <option value="">Select a reference</option>
                  <option value="Craigslist">Craigslist</option>
                  <option value="LeapFrog Employee">LeapFrog Employee</option>
                  <option value="Friend">Friend</option>
                  <option value="Newspaper Ad">Newspaper Ad</option>
                  <option value="Flyer">Flyer</option>
                  <option value="Berkeley Parents Network">
                    Berkeley Parents Network
                  </option>
                  <option value="Kindercare">Kindercare</option>
                  <option value="Golden Gate Mothers Group">
                    Golden Gate Mothers Group
                  </option>
                  <option value="510 Families">510 Families</option>
                  <option value="East Bay Preschool Fair">
                    East Bay Preschool Fair
                  </option>
                  <option value="Other">Other</option>
                </select>
                {errors.referredBy && (
                  <p className="error-message">{errors.referredBy}</p>
                )}
              </div>
            </div>
          </div>

          {/* Guardian Section */}
          <div className="section guardian-section">
            <h3>New Guardian #1</h3>
            <div className="indented-section">
            <div className="stacked-inputs">
              <div className="input-group">
                <label htmlFor="guardianFirstName">
                  First name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="guardianFirstName"
                  value={familyData.guardianFirstName}
                  onChange={handleGuardianChange}
                  required
                  maxLength="31"
                  pattern="[a-zA-Z .'-]+"
                  title="Guardian first name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."
                  className="input-size"
                />
                {errors.guardianFirstName && (
                  <p className="error-message">{errors.guardianFirstName}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="guardianLastName">
                  Last name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="guardianLastName"
                  value={familyData.guardianLastName}
                  onChange={handleGuardianChange}
                  required
                  maxLength="31"
                  pattern="[a-zA-Z .'-]+"
                  title="Guardian last name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."
                  className="input-size"
                />
                {errors.guardianLastName && (
                  <p className="error-message">{errors.guardianLastName}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="relationshipToChild">
                  Relationship <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="relationshipToChild"
                  value={familyData.relationshipToChild}
                  onChange={handleGuardianChange}
                  required
                  className="input-size"
                >
                  <option value="">Select Relationship</option>
                  <option value="Mother">Mother</option>
                  <option value="Father">Father</option>
                  <option value="Grandmother">Grandmother</option>
                  <option value="Grandfather">Grandfather</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Cousin">Cousin</option>
                  <option value="Stepmother">Stepmother</option>
                  <option value="Stepfather">Stepfather</option>
                  <option value="Aunt">Aunt</option>
                  <option value="Uncle">Uncle</option>
                  <option value="Nanny">Nanny</option>
                  <option value="Foster Mother">Foster Mother</option>
                  <option value="Foster Father">Foster Father</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Other">Other</option>
                </select>
                {errors.relationshipToChild && (
                  <p className="error-message">{errors.relationshipToChild}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="familyEmail">
                  Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="email"
                  name="familyEmail"
                  value={familyData.familyEmail}
                  onChange={handleGuardianChange}
                  required
                  maxLength="99"
                  pattern="^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                  title="Email should contain uppercase and lowercase Latin letters A to Z, digits 0 to 9, and printable characters !#$%&'*+-/=?^_`{|}~. It should not start or end with a dot or have consecutive dots."
                  className="input-size"
                />
                {errors.familyEmail && (
                  <p className="error-message">{errors.familyEmail}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="primaryPhone">
                  Phone <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="primaryPhone"
                  value={familyData.primaryPhone}
                  onChange={handlePhoneChange}
                  required
                  maxLength="14"
                  placeholder="(###) ###-####"
                  pattern="\(\d{3}\) \d{3}-\d{4}"
                  title="Primary phone number must be in the format: (123) 456-7890."
                  className="input-size"
                />
                {errors.primaryPhone && (
                  <p className="error-message">{errors.primaryPhone}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="zipCode">
                  Zip code <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={familyData.zipCode}
                  onChange={handleGuardianChange}
                  required
                  maxLength="5"
                  placeholder="#####"
                  pattern="\d{5}"
                  title="Zip Code should contain 5 digits only."
                  className="input-size"
                />
                {errors.zipCode && (
                  <p className="error-message">{errors.zipCode}</p>
                )}
              </div>
            </div>
          </div>
          </div>




          {/* Child Section */}
          <div className="section child-section">
            <h3>New Child #1</h3>
            <div className="indented-section">
            <div className="stacked-inputs">
              <div className="input-group">
                <label htmlFor="childFirstName">
                  Child first name <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="childFirstName"
                  value={familyData.childFirstName}
                  onChange={handleChildChange}
                  required
                  maxLength="31"
                  pattern="[a-zA-Z .'-]+"
                  title="Child first name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."
                  className="input-size"
                />
                {errors.childFirstName && (
                  <p className="error-message">{errors.childFirstName}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="gender">
                  Gender <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="gender"
                  value={familyData.gender}
                  onChange={handleChildChange}
                  required
                  className="input-size"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Decline to state">Decline to state</option>
                </select>
                {errors.gender && (
                  <p className="error-message">{errors.gender}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="dob">
                  Date of birth <span style={{ color: 'red' }}>*</span>
                </label>
                <div className="dob-container">
                  <select
                    name="monthOfBirth"
                    value={familyData.monthOfBirth}
                    onChange={handleChildChange}
                    required
                    className="birth-input-size"
                  >
                    <option value="">Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                  {errors.monthOfBirth && (
                    <p className="error-message">{errors.monthOfBirth}</p>
                  )}
                  <select
                    name="yearOfBirth"
                    value={familyData.yearOfBirth}
                    onChange={handleChildChange}
                    required
                    className="birth-input-size"
                  >
                    <option value="">Year</option>
                    {generateYearOptions()}
                  </select>
                  {errors.yearOfBirth && (
                    <p className="error-message">{errors.yearOfBirth}</p>
                  )}
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="whichHandId">
                  Which hand <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="whichHandId"
                  value={familyData.whichHandId}
                  onChange={handleChildChange}
                  required
                  className="input-size"
                >
                  <option value="">Select Preference</option>
                  <option value="Right-handed">Right-handed</option>
                  <option value="Left-handed">Left-handed</option>
                  <option value="Ambidextrous">Ambidextrous</option>
                  <option value="Don’t know">Don’t know</option>
                </select>
                {errors.whichHandId && (
                  <p className="error-message">{errors.whichHandId}</p>
                )}
              </div>
              <div className="input-group">
                <label htmlFor="languageSpokenAtHome">
                  Language spoken at home{' '}
                </label>
                <select
                  name="languageSpokenAtHome"
                  value={familyData.languageSpokenAtHome}
                  onChange={handleChildChange}
                  required
                  className="input-size"
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Italian">Italian</option>
                  <option value="Greek">Greek</option>
                  <option value="Chinese (Mandarin)">Chinese (Mandarin)</option>
                  <option value="Chinese (Cantonese)">
                    Chinese (Cantonese)
                  </option>
                  <option value="Taiwanese">Taiwanese</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Korean">Korean</option>
                  <option value="ASL">ASL</option>
                  <option value="Tagalog">Tagalog</option>
                  <option value="Telugo">Telugo</option>
                  <option value="Hebrew">Hebrew</option>
                  <option value="Filipino">Filipino</option>
                  <option value="Urdu">Urdu</option>
                  <option value="Russian">Russian</option>
                  <option value="Serbian">Serbian</option>
                  <option value="Portuguese">Portuguese</option>
                  <option value="Armenian">Armenian</option>
                  <option value="Hungarian">Hungarian</option>
                  <option value="Slovak">Slovak</option>
                  <option value="Romanian">Romanian</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="input-group">
                <label htmlFor="additionalLanguage1">
                  Additional Language 1{' '}
                </label>
                <select
                  name="additionalLanguage1"
                  value={familyData.additionalLanguage1}
                  onChange={handleChildChange}
                  className="input-size"
                >
                  <option value="">Select Language</option>
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Italian">Italian</option>
                  <option value="Greek">Greek</option>
                  <option value="Chinese (Mandarin)">Chinese (Mandarin)</option>
                  <option value="Chinese (Cantonese)">
                    Chinese (Cantonese)
                  </option>
                  <option value="Taiwanese">Taiwanese</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Korean">Korean</option>
                  <option value="ASL">ASL</option>
                  <option value="Tagalog">Tagalog</option>
                  <option value="Telugo">Telugo</option>
                  <option value="Hebrew">Hebrew</option>
                  <option value="Filipino">Filipino</option>
                  <option value="Urdu">Urdu</option>
                  <option value="Russian">Russian</option>
                  <option value="Serbian">Serbian</option>
                  <option value="Portuguese">Portuguese</option>
                  <option value="Armenian">Armenian</option>
                  <option value="Hungarian">Hungarian</option>
                  <option value="Slovak">Slovak</option>
                  <option value="Romanian">Romanian</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="input-group">
                <label htmlFor="additionalLanguage2">
                  Additional Language 2{' '}
                </label>
                <select
                  name="additionalLanguage2"
                  value={familyData.additionalLanguage2}
                  onChange={handleChildChange}
                  className="input-size"
                >
                  <option value="">Select Language</option>
                  <option value="English">English</option>
                  <option value="Spanish">Spanish</option>
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Italian">Italian</option>
                  <option value="Greek">Greek</option>
                  <option value="Chinese (Mandarin)">Chinese (Mandarin)</option>
                  <option value="Chinese (Cantonese)">
                    Chinese (Cantonese)
                  </option>
                  <option value="Taiwanese">Taiwanese</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Korean">Korean</option>
                  <option value="ASL">ASL</option>
                  <option value="Tagalog">Tagalog</option>
                  <option value="Telugo">Telugo</option>
                  <option value="Hebrew">Hebrew</option>
                  <option value="Filipino">Filipino</option>
                  <option value="Urdu">Urdu</option>
                  <option value="Russian">Russian</option>
                  <option value="Serbian">Serbian</option>
                  <option value="Portuguese">Portuguese</option>
                  <option value="Armenian">Armenian</option>
                  <option value="Hungarian">Hungarian</option>
                  <option value="Slovak">Slovak</option>
                  <option value="Romanian">Romanian</option>
                  <option value="Other">Other</option>
                </select>
              </div>

            </div>
          </div>
          </div>

          <div className="section">
            <div className="buttons-container">
              <button type="submit" className="submit-button">
                Submit
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </main>
      </form>
    </div>
  );
};

export default AddFamily;
